import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BOM_LABEL, bomTone, pillClass } from "@/lib/factory";
import {
  Plus, X, Trash2, Edit2, Phone, Mail, MapPin, Package,
  Loader2, ChevronRight, Send, MessageSquare,
} from "lucide-react";

/**
 * Vendor Directory (Feb 2026)
 * ---------------------------
 * Central register of every supplier Purchase/Procurement work with. Each
 * vendor row shows contact + GST + notes and a per-vendor history of every
 * BOM item ever ordered from them (aggregated from `bom_items.vendor`).
 *
 * Editable for Purchase Manager / Executive / Procurement / Production /
 * admin / Director. CRM roles land here in read-only mode.
 */
export default function VendorsPage() {
  const { isAdmin, roleName, roleType } = useAuth();
  const canWrite = isAdmin
    || roleType === "director"
    || ["Purchase Manager", "Purchase Executive", "Procurement Manager",
        "Procurement Executive", "Production Manager", "Production Executive"].includes(roleName);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(null);   // null | 'new' | vendor obj
  const [detail, setDetail] = useState(null);       // opened vendor detail

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors");
      setVendors(data.vendors || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (v) => {
    try {
      const { data } = await api.get(`/vendors/${v.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = vendors.filter((v) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [v.name, v.contact_person, v.phone, v.email, v.gst_number]
      .filter(Boolean).some((x) => x.toLowerCase().includes(s));
  });

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Directory</p>
            <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Vendors</h1>
            <p className="text-[11px] text-gray-500 mt-1 italic">
              Every supplier saved for BOM autocomplete — with contact, GST and past-order history.
            </p>
          </div>
          {canWrite && (
            <button onClick={() => setShowForm("new")}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="new-vendor-btn">
              <Plus className="w-3.5 h-3.5"/> New Vendor
            </button>
          )}
        </div>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-4">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, contact, phone, email or GST…"
          className="iz-input w-full text-xs"
          data-testid="vendors-search"
        />
        {loading ? (
          <div className="iz-card p-8 flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading vendors…
          </div>
        ) : filtered.length === 0 ? (
          <div className="iz-card p-10 text-center text-sm text-gray-500 italic">
            {vendors.length === 0
              ? "No vendors saved yet. Add the first one."
              : `No vendor matches "${q}".`}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vendors-grid">
            {filtered.map((v) => (
              <div key={v.id} className="iz-card p-4 group"
                   data-testid={`vendor-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => openDetail(v)}
                          className="text-left flex-1 min-w-0"
                          data-testid={`vendor-open-${v.id}`}>
                    <p className="text-white text-sm truncate">{v.name}</p>
                    {v.contact_person && (
                      <p className="text-[11px] text-gray-500 truncate">{v.contact_person}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-gray-400">
                      {v.phone && (
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{v.phone}</span>
                      )}
                      {v.email && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
                          <Mail className="w-3 h-3"/>{v.email}
                        </span>
                      )}
                      {v.gst_number && (
                        <span className="inline-flex items-center gap-1 font-mono text-[9px] text-[#d4af37]">
                          GST · {v.gst_number}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
                      <Package className="w-3 h-3"/> {v.order_count ?? 0} BOM item(s) on file
                      <ChevronRight className="w-3 h-3 opacity-40 group-hover:opacity-100"/>
                    </p>
                  </button>
                  {canWrite && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => setShowForm(v)}
                              className="p-1 text-gray-500 hover:text-white"
                              title="Edit"
                              data-testid={`vendor-edit-${v.id}`}>
                        <Edit2 className="w-3.5 h-3.5"/>
                      </button>
                      <button onClick={() => remove(v)}
                              className="p-1 text-gray-500 hover:text-red-400"
                              title="Delete"
                              data-testid={`vendor-delete-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <VendorFormModal
          existing={showForm === "new" ? null : showForm}
          onClose={() => setShowForm(null)}
          onSaved={() => { setShowForm(null); load(); }}
        />
      )}
      {detail && (
        <VendorDetailModal
          vendor={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function VendorFormModal({ existing, onClose, onSaved }) {
  const isEdit = Boolean(existing);
  const [form, setForm] = useState({
    name: existing?.name || "",
    contact_person: existing?.contact_person || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    address: existing?.address || "",
    gst_number: existing?.gst_number || "",
    notes: existing?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setErr("Name is required (min 2 chars)");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/vendors/${existing.id}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", form);
        toast.success("Vendor added");
      }
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="vendor-form-modal">
      <div className="iz-card w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              {isEdit ? "Edit Vendor" : "New Vendor"}
            </p>
            <h2 className="font-display text-xl text-white mt-0.5">{form.name || "Untitled vendor"}</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="vendor-form-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="space-y-3">
          {[
            ["name", "Name *", "e.g. Century Ply Bangalore"],
            ["contact_person", "Contact person", "e.g. Ravi Kumar"],
            ["phone", "Phone", "+91-…"],
            ["email", "Email", "orders@vendor.com"],
            ["gst_number", "GST number", "29ABCDE1234F1Z5"],
            ["address", "Address", "Street, City"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
              <input value={form[k]}
                     onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                     placeholder={ph}
                     className="iz-input w-full text-xs"
                     data-testid={`vendor-input-${k}`}/>
            </div>
          ))}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Notes</label>
            <textarea rows={2}
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      className="iz-input w-full text-xs"
                      data-testid="vendor-input-notes"/>
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-400">{err}</p>}

        <div className="mt-5 flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
          <button onClick={save} disabled={busy}
                  className="iz-btn-primary text-xs flex items-center gap-1"
                  data-testid="vendor-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null}
            {isEdit ? "Save changes" : "Add vendor"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorDetailModal({ vendor, onClose }) {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const canWrite = isAdmin || roleType === "director"
    || ["Purchase Manager", "Purchase Executive", "Procurement Manager",
        "Procurement Executive", "Production Manager", "Production Executive"].includes(roleName);
  const [current, setCurrent] = useState(vendor);
  const history = current.history || [];
  const contactLog = current.contact_log || [];
  const [newNote, setNewNote] = useState("");
  const [newKind, setNewKind] = useState("call");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get(`/vendors/${current.id}`);
      setCurrent(data);
    } catch { /* silent */ }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setBusy(true);
    try {
      await api.post(`/vendors/${current.id}/log`, { note: newNote.trim(), kind: newKind });
      setNewNote("");
      toast.success("Note added");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const removeNote = async (entryId) => {
    if (!window.confirm("Remove this note?")) return;
    try {
      await api.delete(`/vendors/${current.id}/log/${entryId}`);
      toast.success("Removed");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="vendor-detail-modal">
      <div className="iz-card w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Vendor</p>
            <h2 className="font-display text-2xl text-white mt-0.5">{current.name}</h2>
            {current.contact_person && (
              <p className="text-xs text-gray-500 mt-1">{current.contact_person}</p>
            )}
            <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-400">
              {current.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{current.phone}</span>}
              {current.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3"/>{current.email}</span>}
              {current.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{current.address}</span>}
              {current.gst_number && (
                <span className="font-mono text-[10px] text-[#d4af37]">GST · {current.gst_number}</span>
              )}
            </div>
            {current.notes && <p className="mt-2 text-[11px] text-gray-400 italic">{current.notes}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="vendor-detail-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {/* Contact log */}
        <div className="mt-5 mb-6 border-t border-[#1a1a1a] pt-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3 flex items-center gap-2">
            <MessageSquare className="w-3 h-3"/> Contact Log ({contactLog.length})
          </p>
          {canWrite && (
            <div className="iz-card p-3 mb-3 space-y-2" data-testid="vendor-log-form">
              <div className="flex gap-2">
                <select value={newKind} onChange={(e) => setNewKind(e.target.value)}
                        className="iz-input text-xs w-28"
                        data-testid="vendor-log-kind">
                  {["call", "email", "meeting", "negotiation", "note"].map((k) =>
                    <option key={k} value={k}>{k}</option>
                  )}
                </select>
                <input value={newNote}
                       onChange={(e) => setNewNote(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                       placeholder="Called Ravi — confirmed dispatch by Fri…"
                       className="iz-input flex-1 text-xs"
                       data-testid="vendor-log-input"/>
                <button onClick={addNote} disabled={busy || !newNote.trim()}
                        className="iz-btn-primary text-xs px-3 flex items-center gap-1"
                        data-testid="vendor-log-submit">
                  <Send className="w-3.5 h-3.5"/>
                </button>
              </div>
              <p className="text-[10px] text-gray-600 italic">Timestamps + author are captured automatically. Press Enter to save.</p>
            </div>
          )}
          {contactLog.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No conversations logged yet.</p>
          ) : (
            <ul className="space-y-2">
              {contactLog.map((entry) => {
                const mine = entry.by_user_id === principal?.id;
                return (
                  <li key={entry.id} className="border border-[#161616] rounded px-3 py-2"
                      data-testid={`vendor-log-${entry.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
                          {entry.kind && (
                            <span className="text-[#d4af37]">{entry.kind}</span>
                          )}
                          <span className="text-gray-500">·</span>
                          <span className="text-gray-400">{entry.by_user_name}</span>
                          <span className="text-gray-600">·</span>
                          <span className="text-gray-600">{new Date(entry.at).toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-gray-200 mt-1 whitespace-pre-wrap">{entry.note}</p>
                      </div>
                      {(mine || isAdmin) && (
                        <button onClick={() => removeNote(entry.id)}
                                className="p-1 text-gray-600 hover:text-red-400 shrink-0"
                                title="Remove note"
                                data-testid={`vendor-log-remove-${entry.id}`}>
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-4 mb-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Past BOM items ({history.length})
        </p>
        {history.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No BOM items ordered from this vendor yet.</p>
        ) : (
          <div className="overflow-x-auto border border-[#1a1a1a] rounded">
            <table className="w-full text-sm" data-testid="vendor-history-table">
              <thead>
                <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a] bg-[#080808]">
                  <th className="text-left px-3 py-2">Project</th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Expected</th>
                  <th className="text-left px-3 py-2">Actual</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-[#0f0f0f]" data-testid={`vendor-history-${h.id}`}>
                    <td className="px-3 py-2 text-white text-xs">
                      <div className="truncate max-w-[220px]">{h.client_name || "—"}</div>
                      <div className="text-[10px] text-gray-500 truncate max-w-[220px]">{h.project_name || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-300 text-xs">{h.item_name}</td>
                    <td className="px-3 py-2 text-right text-white font-mono text-xs whitespace-nowrap">
                      {h.qty}{h.unit ? ` ${h.unit}` : ""}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 border ${pillClass(bomTone(h.status))}`}>
                        {BOM_LABEL[h.status] || h.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-gray-400">{h.expected_delivery_date || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-gray-400">{h.actual_delivery_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
